/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { Pencil, Plus, ShieldCheck, Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { SectionPageLayout } from '@/components/layout'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs'

import {
  createBankAccountConfig,
  deleteBankAccountConfig,
  getBankAccountConfigs,
  getBankDeposits,
  getSmsWebhookLogs,
  manualMatchDeposit,
  updateBankAccountConfig,
} from './api'
import type {
  BankAccountConfig,
  BankDeposit,
  SmsWebhookLog,
} from './types'

function StatusBadge({ status }: { status: string }) {
  const variant =
    status === 'success'
      ? 'default'
      : status === 'ignored'
        ? 'secondary'
        : 'destructive'
  return <Badge variant={variant}>{status}</Badge>
}

function SmsLogsTab() {
  const { t } = useTranslation()
  const [logs, setLogs] = useState<SmsWebhookLog[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    getSmsWebhookLogs()
      .then(setLogs)
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <p className='text-muted-foreground p-4'>{t('Loading...')}</p>

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t('Time')}</TableHead>
          <TableHead>{t('From')}</TableHead>
          <TableHead>{t('Message')}</TableHead>
          <TableHead>{t('Status')}</TableHead>
          <TableHead>{t('Detail')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {logs.length === 0 ? (
          <TableRow>
            <TableCell colSpan={5} className='text-muted-foreground text-center'>
              {t('No SMS webhook activity yet.')}
            </TableCell>
          </TableRow>
        ) : (
          logs.map((log) => (
            <TableRow key={log.id}>
              <TableCell className='whitespace-nowrap'>
                {log.received_at_readable}
              </TableCell>
              <TableCell>{log.from || '-'}</TableCell>
              <TableCell className='max-w-xs truncate' title={log.raw_message}>
                {log.raw_message || '-'}
              </TableCell>
              <TableCell>
                <StatusBadge status={log.parse_status} />
              </TableCell>
              <TableCell className='text-muted-foreground text-sm'>
                {log.parse_detail}
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  )
}

function BankDepositsTab() {
  const { t } = useTranslation()
  const [deposits, setDeposits] = useState<BankDeposit[]>([])
  const [loading, setLoading] = useState(true)
  const [matchDialogDeposit, setMatchDialogDeposit] = useState<BankDeposit | null>(null)
  const [matchUserId, setMatchUserId] = useState('')
  const [matching, setMatching] = useState(false)

  function loadDeposits() {
    setLoading(true)
    getBankDeposits()
      .then(setDeposits)
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadDeposits()
  }, [])

  async function handleManualMatch() {
    if (!matchDialogDeposit) return
    const userId = Number(matchUserId)
    if (!userId) return
    setMatching(true)
    try {
      await manualMatchDeposit(matchDialogDeposit.id, userId)
      setMatchDialogDeposit(null)
      setMatchUserId('')
      loadDeposits()
    } finally {
      setMatching(false)
    }
  }

  if (loading) return <p className='text-muted-foreground p-4'>{t('Loading...')}</p>

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('Received')}</TableHead>
            <TableHead>{t('Amount (Toman)')}</TableHead>
            <TableHead>{t('Account')}</TableHead>
            <TableHead>{t('Date')}</TableHead>
            <TableHead>{t('Time')}</TableHead>
            <TableHead>{t('Bank')}</TableHead>
            <TableHead>{t('Status')}</TableHead>
            <TableHead>{t('Used By')}</TableHead>
            <TableHead className='text-right'>{t('Actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {deposits.length === 0 ? (
            <TableRow>
              <TableCell colSpan={9} className='text-muted-foreground text-center'>
                {t('No bank deposits recorded yet.')}
              </TableCell>
            </TableRow>
          ) : (
            deposits.map((d) => (
              <TableRow key={d.id}>
                <TableCell className='whitespace-nowrap'>
                  {d.received_at_readable}
                </TableCell>
                <TableCell>{d.amount_toman.toLocaleString()}</TableCell>
                <TableCell>{d.account_number || '-'}</TableCell>
                <TableCell>{d.bank_date}</TableCell>
                <TableCell>{d.transfer_time}</TableCell>
                <TableCell>{d.bank_name}</TableCell>
                <TableCell>
                  <Badge variant={d.is_used ? 'default' : 'secondary'}>
                    {d.is_used ? t('Claimed') : t('Unclaimed')}
                  </Badge>
                </TableCell>
                <TableCell>{d.used_by_user_id ? `#${d.used_by_user_id}` : '-'}</TableCell>
                <TableCell className='text-right'>
                  {!d.is_used && (
                    <Button
                      size='sm'
                      variant='outline'
                      onClick={() => setMatchDialogDeposit(d)}
                    >
                      <ShieldCheck className='mr-1 h-4 w-4' />
                      {t('Manual Match')}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <Dialog
        open={matchDialogDeposit !== null}
        onOpenChange={(open) => !open && setMatchDialogDeposit(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('Manually match deposit to a user')}</DialogTitle>
          </DialogHeader>
          {matchDialogDeposit && (
            <div className='space-y-3'>
              <p className='text-muted-foreground text-sm'>
                {t('Deposit')} #{matchDialogDeposit.id}:{' '}
                {matchDialogDeposit.amount_toman.toLocaleString()} {t('Toman')} —{' '}
                {matchDialogDeposit.bank_date} {matchDialogDeposit.transfer_time} —{' '}
                {t('Account')}: {matchDialogDeposit.account_number || '-'}
              </p>
              <div className='space-y-1'>
                <Label>{t('User ID to credit')}</Label>
                <Input
                  type='number'
                  value={matchUserId}
                  onChange={(e) => setMatchUserId(e.target.value)}
                  placeholder='e.g. 5'
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant='outline' onClick={() => setMatchDialogDeposit(null)}>
              {t('Cancel')}
            </Button>
            <Button onClick={handleManualMatch} disabled={matching || !matchUserId}>
              {t('Credit this user')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

const emptyForm: Omit<BankAccountConfig, 'id'> = {
  account_number: '',
  bank_name: '',
  card_number: '',
  owner_name: '',
  description: '',
  enabled: true,
  sort_order: 0,
}

function BankAccountsTab() {
  const { t } = useTranslation()
  const [accounts, setAccounts] = useState<BankAccountConfig[]>([])
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [form, setForm] = useState(emptyForm)
  const [saving, setSaving] = useState(false)

  function loadAccounts() {
    setLoading(true)
    getBankAccountConfigs()
      .then(setAccounts)
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadAccounts()
  }, [])

  function openCreate() {
    setEditingId(null)
    setForm(emptyForm)
    setDialogOpen(true)
  }

  function openEdit(acc: BankAccountConfig) {
    setEditingId(acc.id)
    setForm({
      account_number: acc.account_number,
      bank_name: acc.bank_name,
      card_number: acc.card_number,
      owner_name: acc.owner_name,
      description: acc.description,
      enabled: acc.enabled,
      sort_order: acc.sort_order,
    })
    setDialogOpen(true)
  }

  async function handleSave() {
    setSaving(true)
    try {
      if (editingId === null) {
        await createBankAccountConfig(form)
      } else {
        await updateBankAccountConfig(editingId, form)
      }
      setDialogOpen(false)
      loadAccounts()
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete(id: number) {
    if (!window.confirm(t('Delete this bank account?'))) return
    await deleteBankAccountConfig(id)
    loadAccounts()
  }

  if (loading) return <p className='text-muted-foreground p-4'>{t('Loading...')}</p>

  return (
    <>
      <div className='mb-3 flex justify-end'>
        <Button size='sm' onClick={openCreate}>
          <Plus className='mr-1 h-4 w-4' />
          {t('Add Account')}
        </Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('Bank')}</TableHead>
            <TableHead>{t('Account #')}</TableHead>
            <TableHead>{t('Card #')}</TableHead>
            <TableHead>{t('Owner')}</TableHead>
            <TableHead>{t('Enabled')}</TableHead>
            <TableHead className='text-right'>{t('Actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {accounts.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className='text-muted-foreground text-center'>
                {t('No bank accounts configured yet.')}
              </TableCell>
            </TableRow>
          ) : (
            accounts.map((acc) => (
              <TableRow key={acc.id}>
                <TableCell>{acc.bank_name}</TableCell>
                <TableCell>{acc.account_number}</TableCell>
                <TableCell>{acc.card_number || '-'}</TableCell>
                <TableCell>{acc.owner_name || '-'}</TableCell>
                <TableCell>
                  <Badge variant={acc.enabled ? 'default' : 'secondary'}>
                    {acc.enabled ? t('Enabled') : t('Disabled')}
                  </Badge>
                </TableCell>
                <TableCell className='space-x-1 text-right'>
                  <Button size='icon' variant='ghost' onClick={() => openEdit(acc)}>
                    <Pencil className='h-4 w-4' />
                  </Button>
                  <Button size='icon' variant='ghost' onClick={() => handleDelete(acc.id)}>
                    <Trash2 className='h-4 w-4' />
                  </Button>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingId === null ? t('Add Bank Account') : t('Edit Bank Account')}
            </DialogTitle>
          </DialogHeader>
          <div className='space-y-3'>
            <div className='space-y-1'>
              <Label>{t('Bank name')}</Label>
              <Input
                value={form.bank_name}
                onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
              />
            </div>
            <div className='space-y-1'>
              <Label>{t('Account number (must match SMS "حساب:" field)')}</Label>
              <Input
                value={form.account_number}
                onChange={(e) => setForm({ ...form, account_number: e.target.value })}
              />
            </div>
            <div className='space-y-1'>
              <Label>{t('Card number')}</Label>
              <Input
                value={form.card_number}
                onChange={(e) => setForm({ ...form, card_number: e.target.value })}
              />
            </div>
            <div className='space-y-1'>
              <Label>{t('Owner name')}</Label>
              <Input
                value={form.owner_name}
                onChange={(e) => setForm({ ...form, owner_name: e.target.value })}
              />
            </div>
            <div className='space-y-1'>
              <Label>{t('Description (shown to users)')}</Label>
              <Input
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
            <div className='flex items-center justify-between'>
              <Label>{t('Enabled')}</Label>
              <Switch
                checked={form.enabled}
                onCheckedChange={(checked) => setForm({ ...form, enabled: checked })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setDialogOpen(false)}>
              {t('Cancel')}
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {t('Save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export function BankReports() {
  const { t } = useTranslation()
  return (
    <SectionPageLayout fixedContent>
      <SectionPageLayout.Title>
        {t('Bank Deposit Reports')}
      </SectionPageLayout.Title>
      <SectionPageLayout.Content>
        <Tabs defaultValue='deposits'>
          <TabsList>
            <TabsTrigger value='deposits'>{t('Deposits')}</TabsTrigger>
            <TabsTrigger value='sms-logs'>{t('SMS Logs')}</TabsTrigger>
            <TabsTrigger value='accounts'>{t('Bank Accounts')}</TabsTrigger>
          </TabsList>
          <TabsContent value='deposits'>
            <BankDepositsTab />
          </TabsContent>
          <TabsContent value='sms-logs'>
            <SmsLogsTab />
          </TabsContent>
          <TabsContent value='accounts'>
            <BankAccountsTab />
          </TabsContent>
        </Tabs>
      </SectionPageLayout.Content>
    </SectionPageLayout>
  )
}
